import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminUserCreated, UserRole } from 'shared'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
} from '../ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

interface NewUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (created: AdminUserCreated) => void
}

/**
 * Crea un account. L'admin non sceglie la password di nessuno: la persona la
 * sceglie da sé, con il link che compare subito dopo.
 */
export default function NewUserDialog({ open, onOpenChange, onCreated }: NewUserDialogProps) {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<UserRole>('operator')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setEmail('')
    setName('')
    setRole('operator')
    setError(null)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const created = await api.post<AdminUserCreated>('/api/admin/users', {
        email: email.trim(),
        name: name.trim(),
        role,
      })

      reset()
      onOpenChange(false)
      onCreated(created)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('admin.newUserTitle')}</DialogTitle>
            <DialogDescription>{t('admin.newUserDescription')}</DialogDescription>
          </DialogHeader>

          {error && <Alert>{error}</Alert>}

          <Field label={t('auth.email')} required>
            <Input
              type="email"
              name="email"
              required
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label={t('auth.name')} required>
            <Input
              name="name"
              required
              autoComplete="off"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label={t('admin.role')} hint={t('admin.newUserRoleHint')}>
            <Select
              value={role}
              onChange={(event) => setRole(event.target.value as UserRole)}
              options={[
                { value: 'member', label: t('admin.role_member') },
                { value: 'operator', label: t('admin.role_operator') },
                { value: 'admin', label: t('admin.role_admin') },
                { value: 'partner', label: t('admin.role_partner') },
              ]}
            />
          </Field>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                reset()
                onOpenChange(false)
              }}
              disabled={submitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {t('admin.createUser')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
